import React from 'react';
import gql from 'graphql-tag';
import { CSP, CoreMarketplaceCspRow, reverb_search_CSPSearchRequest_Sort } from '../../../schemas/rql';
import { graphql, ChildProps } from 'react-apollo';
import { every, isEmpty, get, compact, uniq, flatten } from 'lodash';
import { conditionFilter } from './condition_filter';
import { Location } from 'history';

import CSPRow, { CSPCardFragment } from '@reverbdotcom/commons/src/components/csp_row';
import { MarketplacePathContext } from '../../shared/url_helpers';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import experiments from '@reverbdotcom/commons/src/experiments';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';

interface ExternalProps extends IUserContext {
  title: string;
  location?: Partial<Location>;
  routeParams: MarketplacePathContext;
  trackingQuery?: string;
}

export function propsToSearch(props: ExternalProps): Partial<CoreMarketplaceCspRow.Variables> {
  const { location, routeParams } = props;

  return {
    sort: reverb_search_CSPSearchRequest_Sort.TRENDING_DESC,
    condition: conditionFilter(location.query.condition),
    fullTextQuery: location.query.query,
    categories: uniq(compact([
      location.query.product_type,
      location.query.category,
    ])),
    brands: compact(flatten([location.query.make, routeParams.brand_slug])),
    decades: compact(flatten([location.query.decades])),
    yearMax: location.query.year_max || undefined,
    yearMin: location.query.year_min || undefined,
    traitValues: location.query.trait_values || undefined,
  };
}

type IProps = ChildProps<ExternalProps, CoreMarketplaceCspRow.Query>;

const COMPONENT_NAME = 'MarketplaceCSPRow';

export class MarketplaceCSPRow extends React.Component<IProps, {}> {
  loading() {
    return get(this.props, 'data.loading');
  }

  getCSPs() {
    return get(this.props, 'data.cspSearch.csps', []) as CSP[];
  }

  render() {
    return (
      <CSPRow
        title={this.props.title}
        csps={this.getCSPs()}
        loading={this.loading()}
        conditionFilter={conditionFilter(this.props.location.query.condition)}
        trackingComponentName={COMPONENT_NAME}
        shouldTrack
        trackingQuery={this.props.trackingQuery}
        renderAsCondensed={isExperimentEnabled(this.props.user, experiments.CONDENSED_CSP_CARD)}
      />
    );
  }
}

const connect = graphql<
ExternalProps,
CoreMarketplaceCspRow.Query,
CoreMarketplaceCspRow.Variables
>(
  gql`
    query Core_Marketplace_CspRow(
      $fullTextQuery: String,
      $categories: [String],
      $brands: [String],
      $condition: String,
      $curatedSetSlugs: [String],
      $decades: [String],
      $yearMax: Int,
      $yearMin: Int,
      $sort: reverb_search_CSPSearchRequest_Sort
      $traitValues: [String]
    ) {
      cspSearch(input: {
        fullTextQuery: $fullTextQuery,
        sort: $sort,
        categorySlugs: $categories,
        brandSlugs: $brands,
        condition: $condition,
        curatedSetSlugs: $curatedSetSlugs,
        decades: $decades,
        yearMax: $yearMax,
        yearMin: $yearMin,
        fullTextQueryOperand: AND,
        withInventory: true,
        traitValues: $traitValues
      }) {
        csps {
          _id
          ... CSPCard
         }
      }
    }
    ${CSPCardFragment}
  `,
  {
    skip: (props) => {
      const { shop, curated_set_slug, landing_page_slug } = props.routeParams;

      if (shop || curated_set_slug || landing_page_slug) {
        return true;
      }

      const search = propsToSearch(props);

      const values = Object
        .keys(search)
        .map(k => search[k]);

      return every(values, v => isEmpty(v));
    },
    options: (props) => {
      return {
        variables: {
          ...propsToSearch(props),
        },
      };
    },
  },
);

export default withUserContext(connect(MarketplaceCSPRow));
